import React, { useState } from 'react';
import { Formik } from 'formik';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import * as yup from 'yup';
import {
  Flex,
  Label,
  Input,
  Button,
  FormMessage,
  Box,
  theme,
  Spinner,
  Alert,
  Modal,
} from '@xometry/xometry_loft';
import { css } from '@emotion/react';
import { useMutation } from '@apollo/client';
import { requiredMessage, name } from '../../../utils/validations';
import {
  AddStripeCardMutationResult,
  AddStripeCardMutationVariables,
  AddStripeCardQuery,
} from '../../../schema/generated/schema';

const schema = yup.object().shape({
  cardHolderName: name.required(requiredMessage),
});

export interface Props {
  methodsRefetch: any;
  addCardModalOpen: boolean;
  setAddCardModalOpen: (openState: boolean) => void;
}

const AddCardModal = (props: Props) => {
  const { methodsRefetch, addCardModalOpen, setAddCardModalOpen } = props;
  const [cardError, setCardError] = useState(false);
  const [cardErrorMessage, setCardErrorMessage] = useState('');

  const [addStripeCard] = useMutation<
    AddStripeCardMutationResult,
    AddStripeCardMutationVariables
  >(AddStripeCardQuery);

  const stripe = useStripe();
  const elements = useElements();

  const cardStyle = {
    style: {
      base: {
        fontSize: theme.fontSizes[2],
        font: theme.fonts[0],
        lineHeight: theme.lineHeights[3],
        outline: 0,
        color: theme.colors.darkBlues[90],
        boxSizing: 'border-box',
        ':-webkit-appearance': 'none',
        '::placeholder': {
          opacity: 1,
          color: theme.colors.darkBlues[25],
        },
      },
      invalid: {
        borderColor: theme.colors.reds[100],
        boxShadow: `0 0 4px 1px ${theme.colors.reds[50]}`,
        backgroundColor: theme.colors.reds[10],
      },
      disabled: {
        backgroundColor: theme.colors.darkBlues[10],
        border: `${theme.borders[0]} solid ${theme.colors.darkBlues[50]}`,
      },
    },
  };

  return (
    <>
      <div id="addCardModal" />
      <Modal
        isOpen={addCardModalOpen}
        selector="#addCardModal"
        padding="20px 20px 20px 24px"
        color="#253842"
        fontFamily="Open Sans"
        backgroundColor="white"
        borderRadius="4px"
        border="none"
        boxShadow="0 1px 4px 0 rgba(9,44,71,0.15)"
        width="344px"
        display={addCardModalOpen ? 'initial' : 'none'}
      >
        <Flex justifyContent="space-between">
          <Box
            fontSize={theme.fontSizes[3]}
            fontWeight={theme.fontWeights.semibold}
            lineHeight="28px"
            color={theme.colors.darkBlues[90]}
          >
            Add a Debit or Credit Card
          </Box>
          <Box
            fontSize={theme.fontSizes[4]}
            lineHeight={theme.lineHeights[3]}
            textAlign="right"
            color={theme.colors.darkBlues[100]}
            onClick={() => setAddCardModalOpen(false)}
            css={css`
              &:hover {
                cursor: pointer;
              }
            `}
          >
            <i className="fal fa-times" />
          </Box>
        </Flex>
        <Box fontSize={theme.fontSizes[0]} color={theme.colors.darkBlues[75]}>
          Once added, you can use this card for future payments.
        </Box>
        <Box marginTop="24px">
          <Formik
            initialValues={{
              cardHolderName: '',
            }}
            validationSchema={schema}
            onSubmit={(values, { setSubmitting }) => {
              if (!stripe || !elements) {
                return;
              }
              setSubmitting(true);

              const cardElement = elements.getElement(CardElement);
              if (cardElement) {
                stripe
                  .createToken(cardElement, {
                    name: values.cardHolderName,
                  })
                  .then((result: any) => {
                    const stripeTokenId = result.token.id;
                    return addStripeCard({
                      variables: {
                        token: stripeTokenId,
                      },
                    });
                  })
                  .then(() => {
                    methodsRefetch();
                    cardElement?.clear();
                    setSubmitting(false);
                    setAddCardModalOpen(false);
                    setCardError(false);
                  })
                  .catch((error: any) => {
                    setSubmitting(false);
                    setCardError(true);

                    if (error.message.includes('card')) {
                      setCardErrorMessage(error.message);
                    }
                  });
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              /* and other goodies */
            }) => (
              <form onSubmit={handleSubmit}>
                <Label htmlFor="cardHolderName" marginTop="16px">
                  Name On Card
                </Label>
                <Input
                  className="pii-encrypt"
                  id="cardHolderName"
                  width="fill-available"
                  scale="large"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.cardHolderName}
                  invalid={Boolean(
                    touched.cardHolderName && errors.cardHolderName
                  )}
                />
                {Boolean(touched.cardHolderName && errors.cardHolderName) && (
                  <FormMessage invalid>{errors.cardHolderName}</FormMessage>
                )}
                <Label htmlFor="cardDetails" marginTop="16px">
                  Card Details
                </Label>
                <Box
                  height="27px"
                  padding="4px 8px"
                  paddingTop="9px"
                  border={`${theme.borders[0]} solid ${theme.colors.darkBlues[50]}`}
                  borderRadius="3px"
                >
                  <CardElement className="pii-block" options={cardStyle} />
                </Box>
                {cardError && (
                  <Alert
                    kind="danger"
                    marginTop="16px"
                    fontSize={theme.fontSizes[1]}
                    isOpen
                  >
                    {cardErrorMessage || 'There was an error adding your card'}
                  </Alert>
                )}
                <Flex justifyContent="flex-end" marginTop="24px">
                  {!isSubmitting && (
                    <>
                      <Button
                        disabled={isSubmitting}
                        marginRight="8px"
                        type="button"
                        onClick={() => setAddCardModalOpen(false)}
                        border="none"
                      >
                        Cancel
                      </Button>
                      <Button
                        kind="primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        Add Card
                      </Button>
                    </>
                  )}
                  {isSubmitting && <Spinner />}
                </Flex>
              </form>
            )}
          </Formik>
        </Box>
      </Modal>
    </>
  );
};

export default AddCardModal;
